import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  VacationFormData,
  VacationWidgetFormType
} from '@com-delta-idp/mnx-sitewide-header-ui';
import * as Constants from '../../constants/booking-vacation/booking-vacation.constants';
import { VacationSearchRequest } from '../../models/interface/booking-vacation/booking-vacation.interfaces';

@Injectable({ providedIn: 'root' })
export class BookingVacationFormService {
  private passengerRoomValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const passengersControl = control.get('numberOfPassengers');
    const passengers = passengersControl?.value;
    const rooms = control.get('numberOfRooms')?.value;

    if (!passengers || !rooms) return null;

    const hasError = passengers < rooms;
    passengersControl?.setErrors(
      hasError
        ? { passengerRoomMismatch: { min: rooms, current: passengers } }
        : null
    );
    return null;
  }

  private originDestinationValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const originControl = control.get('originAirportCode');
    const destinationControl = control.get('destinationAirportCode');
    const origin = originControl?.value?.trim().toUpperCase();
    const destination = destinationControl?.value?.trim().toUpperCase();

    if (!origin || !destination) return null;
    if (origin === destination) {
      destinationControl?.setErrors({ sameAirport: { origin, destination } });
    }
    return null;
  }

  createBookingForm(): FormGroup<VacationWidgetFormType> {
    return new FormGroup(
      {
        originAirportCode: new FormControl('', {
          validators: [Validators.required],
          nonNullable: true
        }),
        destinationAirportCode: new FormControl('', {
          validators: [Validators.required],
          nonNullable: true
        }),
        departDate: new FormControl<Date | null>(null, {
          validators: [Validators.required]
        }),
        returnDate: new FormControl<Date | null>(null, {
          validators: [Validators.required]
        }),
        numberOfPassengers: new FormControl(1, {
          validators: [Validators.required],
          nonNullable: true
        }),
        numberOfRooms: new FormControl(1, {
          validators: [Validators.required],
          nonNullable: true
        })
      },
      {
        validators: [
          this.passengerRoomValidator,
          this.dateValidator,
          this.originDestinationValidator
        ]
      }
    );
  }

  distributePassengersToRooms(
    numberOfPassengers: number,
    numberOfRooms: number
  ): { adultPassenger: number; childPassenger: number }[] {
    if (numberOfRooms === 1) {
      return [{ adultPassenger: numberOfPassengers, childPassenger: 0 }];
    }
    const basePassengers = Math.floor(numberOfPassengers / numberOfRooms);
    const extraPassengers = numberOfPassengers % numberOfRooms;
    return Array.from({ length: numberOfRooms }, (_, i) => ({
      adultPassenger: Math.max(
        1,
        basePassengers + (i < extraPassengers ? 1 : 0)
      ),
      childPassenger: 0
    }));
  }

  createSearchObject(
    formValues: VacationFormData,
    rooms: { adultPassenger: number; childPassenger: number }[]
  ): VacationSearchRequest {
    return {
      originCode: formValues.originAirportCode,
      destinationCode: formValues.destinationAirportCode,
      departureDate: formValues.departDate.toISOString().split('T')[0],
      returnDate: formValues.returnDate.toISOString().split('T')[0],
      pkgType: Constants.AIR_HOTEL,
      lifestyleType: Constants.CORE,
      marketingCampaignId: '',
      promoCode: '',
      smNumber: '',
      rooms
    };
  }

  navigateToVacationSearch(searchObject: VacationSearchRequest): void {
    const url = `${
      window.location.origin
    }/delta-vacation/search/meta-search?search=${encodeURIComponent(
      JSON.stringify(searchObject)
    )}`;
    window.open(url, '_blank');
  }

  private dateValidator(control: AbstractControl): ValidationErrors | null {
    const departDateControl = control.get('departDate');
    const returnDateControl = control.get('returnDate');
    const departDate = departDateControl?.value;
    const returnDate = returnDateControl?.value;

    if (!departDate || !returnDate) return null;

    const diffInMs = returnDate.getTime() - departDate.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays > 28) {
      returnDateControl?.setErrors({
        maxDateMismatch: { min: returnDate, current: departDate }
      });
    }
    return null;
  }
}
